import React from "react";
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SwipeLeftIcon from "@mui/icons-material/SwipeLeft";
import { alpha, type Theme } from "@mui/material/styles";
import { ProfileSectionTitle } from "../components/profile/ProfileSectionTitle";
import { MiContenidoPageHeader } from "../components/profile/MiContenidoPageHeader";
import { themeConstants } from "../theme";
import { useTranslation } from "react-i18next";
import { MiContenidoSubpageShell } from "./MiContenidoSubpageShell";

const RANKING_SKELETON_ROWS = 5;

const rankingHeadCellSx = {
  bgcolor: "action.hover",
  borderBottom: "1px solid",
  borderColor: "divider",
} as const;

const RANKING_COLUMNS = [
  { key: "name", width: 96 },
  { key: "videoCount", width: 56 },
  { key: "totalViews", width: 56 },
  { key: "avgViews", width: 56 },
  { key: "totalInteractions", width: 56 },
  { key: "avgInteractions", width: 56 },
  { key: "streamCount", width: 56 },
  { key: "avgViewers", width: 56 },
  { key: "peakViewers", width: 56 },
  { key: "rankPodscope", width: 56 },
] as const;

function programRankingTableContainerSx(theme: Theme) {
  return {
    bgcolor: "background.paper",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: themeConstants.border.radius,
    boxShadow: `0 1px 2px ${alpha(
      theme.palette.primary.main,
      theme.palette.mode === "dark" ? 0.12 : 0.08,
    )}`,
    overflowX: "auto",
  };
}

function SkeletonSectionTitle({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation("miContenido");
  return <ProfileSectionTitle title={t(titleKey)} />;
}

function RankingSwipeHint() {
  const { t } = useTranslation("miContenido");
  return (
    <Box
      sx={{
        display: { xs: "flex", md: "none" },
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 1,
        color: "text.secondary",
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <SwipeLeftIcon fontSize="small" />
      <Typography variant="caption">{t("actions.swipeForMore")}</Typography>
    </Box>
  );
}

/** 96×96 avatar skeleton for subpage headers. */
export function MiContenidoIdentitySkeleton() {
  return (
    <Skeleton
      variant="rectangular"
      width={96}
      height={96}
      sx={{ borderRadius: 2, flexShrink: 0 }}
    />
  );
}

/** Title row for subpage headers — matches MiContenidoPageHeader h1 line height. */
export function MiContenidoHeaderTitleSkeleton() {
  return (
    <Skeleton
      variant="text"
      width={240}
      sx={{
        display: "block",
        transform: "none",
        fontSize: { xs: "1.5rem", md: "1.75rem" },
        lineHeight: 1.2,
        minHeight: "1.2em",
      }}
    />
  );
}

export function MiContenidoHeaderActionsSkeleton() {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Skeleton variant="rounded" width={120} height={36} />
      <Skeleton variant="rounded" width={160} height={36} />
    </Stack>
  );
}

/** Mirrors ProgramPlatformStatCard / ProfilePlatformMetricCard layout. */
export function MiContenidoPlatformStatCardSkeleton() {
  return (
    <Card
      elevation={0}
      sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          sx={{ flexShrink: 0 }}
        />
        <Skeleton variant="text" width={96} sx={{ fontSize: "0.8125rem" }} />
      </Stack>
      <Grid container spacing={1}>
        {["a", "b", "c", "d"].map((k) => (
          <Grid size={{ xs: 6 }} key={k}>
            <Typography variant="caption" display="block">
              <Skeleton width="60%" />
            </Typography>
            <Typography variant="body2">
              <Skeleton width="45%" />
            </Typography>
          </Grid>
        ))}
      </Grid>
    </Card>
  );
}

/** 6-card platform metrics grid — matches ProgramStatsOverview and home Plataformas. */
export function MiContenidoPlatformMetricsSkeleton() {
  return (
    <Grid container spacing={2} columns={{ xs: 1, sm: 2, lg: 3, xl: 6 }}>
      {["a", "b", "c", "d", "e", "f"].map((k) => (
        <Grid size={{ xs: 1 }} key={k}>
          <MiContenidoPlatformStatCardSkeleton />
        </Grid>
      ))}
    </Grid>
  );
}

const METRICS_COMPARISON_TABLE_SKELETON_ROWS = 5;
const METRICS_COMPARISON_TABLE_SKELETON_COLS = 6;

/** Comparison table on metrics detail (Métricas de contenido / Podscope). */
export function MetricsComparisonTableSkeleton() {
  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow>
            {Array.from({ length: METRICS_COMPARISON_TABLE_SKELETON_COLS }).map(
              (_, col) => (
                <TableCell
                  key={col}
                  sx={{
                    py: 1.25,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Skeleton
                    variant="rounded"
                    width={col === 0 ? 88 : 72}
                    height={14}
                  />
                </TableCell>
              ),
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {Array.from({ length: METRICS_COMPARISON_TABLE_SKELETON_ROWS }).map(
            (_, row) => (
              <TableRow key={row}>
                {Array.from({
                  length: METRICS_COMPARISON_TABLE_SKELETON_COLS,
                }).map((_, col) => (
                  <TableCell key={col} sx={{ py: 1.25 }}>
                    <Skeleton
                      variant="rounded"
                      width={col === 0 ? 96 : 56}
                      height={14}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ),
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** @deprecated Use MiContenidoPlatformMetricsSkeleton */
export const MiContenidoMetricsSkeleton = MiContenidoPlatformMetricsSkeleton;

/** 4-column hero strip inside a Card — home dashboard. */
export function MiContenidoHeroStripSkeleton() {
  return (
    <Card elevation={0} sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{ display: { xs: "none", sm: "block" } }}
          />
        }
        spacing={{ xs: 2, sm: 0 }}
      >
        {["views", "interactions", "content", "brands"].map((k) => (
          <Box key={k} sx={{ flex: 1, px: 1, textAlign: { xs: "left", sm: "center" } }}>
            <Skeleton variant="text" width="60%" height={16} sx={{ mx: { sm: "auto" } }} />
            <Skeleton variant="text" width="40%" height={32} sx={{ mx: { sm: "auto" }, mt: 0.5 }} />
          </Box>
        ))}
      </Stack>
    </Card>
  );
}

export function MiContenidoChannelsSkeleton() {
  return (
    <Grid
      container
      spacing={2}
      columns={{ xs: 1, sm: 2, md: 3, lg: 5, xl: 5 }}
    >
      {[0, 1, 2, 3, 4].map((i) => (
        <Grid
          key={i}
          size={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
          sx={{ display: "flex", width: "100%" }}
        >
          <Skeleton
            variant="rectangular"
            height={120}
            sx={{ borderRadius: "8px", width: "100%" }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export function MiContenidoProgramListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <Stack spacing={2}>
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton
          key={i}
          variant="rectangular"
          height={92}
          sx={{ borderRadius: "8px" }}
        />
      ))}
    </Stack>
  );
}

export type MiContenidoRankingTableSkeletonProps = {
  showChannelChips?: boolean;
  showSectionTitle?: boolean;
};

export function MiContenidoRankingTableSkeleton({
  showChannelChips = false,
  showSectionTitle = true,
}: MiContenidoRankingTableSkeletonProps) {
  return (
    <Stack spacing={3}>
      {showSectionTitle ? (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={1}
        >
          <SkeletonSectionTitle titleKey="sections.programPerformance" />
          <Skeleton variant="rounded" width={120} height={36} />
        </Stack>
      ) : null}
      <TableContainer sx={(theme) => programRankingTableContainerSx(theme)}>
        <RankingSwipeHint />
        <Table size="small" sx={{ minWidth: 640 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36, px: 1.5, py: 1, ...rankingHeadCellSx }} />
              {RANKING_COLUMNS.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{ py: 1, ...rankingHeadCellSx }}
                >
                  <Skeleton variant="rounded" width={col.width} height={14} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: RANKING_SKELETON_ROWS }).map((_, row) => (
              <TableRow key={row}>
                <TableCell sx={{ px: 1.5, py: 1, width: 36 }}>
                  <Skeleton variant="circular" width={24} height={24} />
                </TableCell>
                <TableCell sx={{ py: 1 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ minWidth: 0 }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Skeleton variant="rounded" width={28} height={28} />
                      <Skeleton variant="text" width={140} height={20} />
                    </Stack>
                    {showChannelChips ? (
                      <Skeleton variant="rounded" width={72} height={22} sx={{ flexShrink: 0 }} />
                    ) : null}
                  </Stack>
                </TableCell>
                {RANKING_COLUMNS.slice(1).map((col) => (
                  <TableCell key={col.key} align="right" sx={{ py: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      <Skeleton variant="rounded" width={44} height={20} />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

export function MiContenidoTrendsSkeleton() {
  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Skeleton variant="text" width={120} height={32} />
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={140} height={36} />
          <Skeleton variant="rounded" width={100} height={36} />
        </Stack>
      </Stack>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        {["yt-long", "yt-shorts", "podscope"].map((k) => (
          <Skeleton
            key={k}
            variant="rectangular"
            height={280}
            sx={{ borderRadius: "8px", flex: 1, minWidth: 0 }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export function MiContenidoContentGridSkeleton() {
  return (
    <Grid container spacing={2} columns={{ xs: 4, sm: 6, md: 9, lg: 10 }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <Grid size={{ xs: 4, sm: 3, md: 3, lg: 2 }} key={i}>
          <Skeleton
            variant="rectangular"
            height={280}
            sx={{ borderRadius: "8px" }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export function MiContenidoContentSectionSkeleton({
  showSort = true,
}: {
  showSort?: boolean;
} = {}) {
  return (
    <Stack spacing={2}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={1}
          sx={{ pb: 1 }}
        >
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
            {["youtube", "shorts", "instagram", "facebook", "tiktok"].map((k) => (
              <Skeleton key={k} variant="rounded" width={100} height={36} />
            ))}
          </Stack>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            useFlexGap
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Skeleton
              variant="rounded"
              height={40}
              sx={{ flex: 1, minWidth: { xs: 0, sm: 220 }, width: { xs: "100%", sm: 260 } }}
            />
            {showSort ? (
              <Skeleton
                variant="rounded"
                width={240}
                height={40}
                sx={{ width: { xs: "100%", sm: 240 } }}
              />
            ) : null}
          </Stack>
        </Stack>
      </Box>
      <MiContenidoContentGridSkeleton />
    </Stack>
  );
}

export function MiContenidoCollectionsPreviewSkeleton() {
  return (
    <Stack spacing={3} sx={{ width: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <SkeletonSectionTitle titleKey="sections.collections" />
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={160} height={36} />
          <Skeleton variant="rounded" width={120} height={36} />
        </Stack>
      </Stack>
      <Grid container spacing={2}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Grid size={{ xs: 12, sm: 6, md: 2 }} key={i}>
            <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1, mb: 1 }} />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" />
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

export type MiContenidoDashboardSkeletonProps = {
  companyName?: string;
  showCanales?: boolean;
  showRanking?: boolean;
  showPrograms?: boolean;
  showBrands?: boolean;
  showCollections?: boolean;
  showChannelChips?: boolean;
};

/** Full-page skeleton for /mi-contenido home (lazy chunk load). */
export const MiContenidoDashboardSkeleton: React.FC<
  MiContenidoDashboardSkeletonProps
> = ({
  companyName,
  showCanales = true,
  showRanking = true,
  showPrograms = true,
  showBrands = true,
  showCollections = true,
  showChannelChips = false,
}) => (
  <Box sx={{ bgcolor: "background.default", width: "100%" }}>
    <Container maxWidth="xl">
      <Stack spacing={4}>
        <MiContenidoPageHeader
          identity={<MiContenidoIdentitySkeleton />}
          title={
            companyName ?? (
              <Skeleton variant="text" width={200} height={36} sx={{ fontSize: "1.75rem" }} />
            )
          }
          actions={<MiContenidoHeaderActionsSkeleton />}
        />

        <MiContenidoHeroStripSkeleton />

        <Stack spacing={4} sx={{ width: "100%" }}>
          {showCanales ? (
            <Stack spacing={3}>
              <SkeletonSectionTitle titleKey="sections.channels" />
              <MiContenidoChannelsSkeleton />
            </Stack>
          ) : null}

          <Stack spacing={3}>
            <SkeletonSectionTitle titleKey="sections.platforms" />
            <MiContenidoPlatformMetricsSkeleton />
          </Stack>

          {showRanking ? (
            <MiContenidoRankingTableSkeleton showChannelChips={showChannelChips} />
          ) : null}

          {showCollections ? <MiContenidoCollectionsPreviewSkeleton /> : null}

          {(showPrograms || showBrands) && (
            <Grid container spacing={3}>
              {showPrograms ? (
                <Grid size={{ xs: 12, md: showBrands ? 6 : 12 }}>
                  <Stack spacing={3}>
                    <SkeletonSectionTitle titleKey="sections.programs" />
                    <MiContenidoProgramListSkeleton />
                  </Stack>
                </Grid>
              ) : null}
              {showBrands ? (
                <Grid size={{ xs: 12, md: showPrograms ? 6 : 12 }}>
                  <Stack spacing={3}>
                    <SkeletonSectionTitle titleKey="sections.brands" />
                    <MiContenidoProgramListSkeleton />
                  </Stack>
                </Grid>
              ) : null}
            </Grid>
          )}
        </Stack>
      </Stack>
    </Container>
  </Box>
);

export type MiContenidoEntityDetailVariant =
  | "program"
  | "podcast"
  | "brand"
  | "metricsDetail"
  | "contentDetail";

export type MiContenidoEntityDetailSkeletonProps = {
  variant?: MiContenidoEntityDetailVariant;
  metricsTitle?: string;
};

/**
 * Full-page skeleton for Mi contenido entity detail routes (lazy chunk load).
 */
export const MiContenidoEntityDetailSkeleton: React.FC<
  MiContenidoEntityDetailSkeletonProps
> = ({
  variant = "program",
  metricsTitle,
}) => {
  const { t } = useTranslation("miContenido");
  const resolvedMetricsTitle = metricsTitle ?? t("sections.programMetrics");
  return (
  <MiContenidoSubpageShell
    identity={<MiContenidoIdentitySkeleton />}
    title={<MiContenidoHeaderTitleSkeleton />}
    actions={<MiContenidoHeaderActionsSkeleton />}
  >
    <Stack spacing={4}>
      {variant === "metricsDetail" ? (
        <MiContenidoPlatformMetricsSkeleton />
      ) : null}
      {variant === "contentDetail" ? (
        <MiContenidoContentSectionSkeleton showSort />
      ) : null}
      {variant !== "brand" && variant !== "metricsDetail" && variant !== "contentDetail" ? (
        <Stack spacing={3}>
          <ProfileSectionTitle title={resolvedMetricsTitle} />
          <MiContenidoPlatformMetricsSkeleton />
        </Stack>
      ) : null}
      {variant !== "metricsDetail" && variant !== "contentDetail" ? (
      <Stack spacing={3}>
        <SkeletonSectionTitle titleKey="sections.content" />
        <MiContenidoContentSectionSkeleton />
      </Stack>
      ) : null}
    </Stack>
  </MiContenidoSubpageShell>
  );
};
